'use client';

import { useCallback, useId, useRef, useState } from 'react';

import cx from 'classnames';
import YouTube from 'react-youtube';

import { type Masked, unmask } from '~/contentful/graphql';
import { Typename } from '~/contentful/graphql/typename';
import { Column, Grid, type GridProps } from '~/ui/components/grid';
import { Breakpoint } from '~/ui/styles/grid';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { useVideoPlayback } from '~/v1/utils/useVideoPlayback';

import { VideoPreview } from './preview';
import { type Video_VideoNativeFragment, type Video_VideoYoutubeFragment } from './query';
import styles from './video.module.scss';

type VideoFragment = typeof Video_VideoNativeFragment | typeof Video_VideoYoutubeFragment;

export interface VideoProps extends GridProps {
  data: Masked<VideoFragment>;
}

export function Video({ data, ...rest }: VideoProps) {
  const video = unmask<VideoFragment>(data);

  const id = useId();
  const videoRef = useRef<HTMLVideoElement>(null);
  const preview =
    video.preview ?? (video.__typename === Typename.VIDEO_NATIVE ? video.video : null);
  // if there is no preview, show the video immediately.
  const [showPreview, setShowPreview] = useState(!!preview);

  const onPlay = useVideoPlayback(videoRef, () => {
    videoRef.current?.pause();
    setShowPreview(true);
  });

  const onPreviewClick = useCallback(() => {
    videoRef.current?.play();
    videoRef.current?.focus();
    setShowPreview(false);
    onPlay();
  }, [onPlay]);

  const isNative = video.__typename === Typename.VIDEO_NATIVE;

  return (
    <Grid {...rest}>
      <Column
        span={{
          [Breakpoint.MOBILE]: 5,
          [Breakpoint.TABLET]: isNative && video.isVertical ? 3 : 5,
          [Breakpoint.DESKTOP]: isNative && video.isVertical ? 3 : 5,
        }}
        offsetLeft={{ [Breakpoint.DESKTOP]: 1 }}
      >
        <figure>
          <div className={styles.wrapper}>
            {isNative ? (
              <video
                src={video.video?.url ?? undefined}
                ref={videoRef}
                className={styles.video}
                controls
                controlsList="nodownload"
                preload="auto"
                crossOrigin="anonymous"
                aria-hidden={showPreview}
                id={id}
              >
                <track
                  src={video.closedCaptions?.url ?? undefined}
                  srcLang="en"
                  label="English"
                  kind="captions"
                  default
                />
              </video>
            ) : (
              <YouTube
                videoId={getVideoId(
                  // @ts-expect-error I'm honestly not sure why this is necessary. TypeScript
                  //  doesn't think the conditional above is sufficient to narrow the type of video to nativevideo
                  video.src,
                )}
                iframeClassName={styles.video}
                aria-hidden={showPreview}
              />
            )}
            {showPreview && preview && (
              <VideoPreview
                videoSrc={preview.url ?? undefined}
                fullVideoRef={videoRef}
                onClick={onPreviewClick}
                isAmbient={isNative ? video.isAmbient : false}
              />
            )}
          </div>
          {!!video.caption && (
            <figcaption className={cx(styles.caption, typographyStyles.caption)}>
              {video.caption}
            </figcaption>
          )}
        </figure>
      </Column>
    </Grid>
  );
}

const getVideoId = (url: string): string => {
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[7].length == 11 ? match[7] : '';
};
